import { useEffect, useRef, useState } from 'react';
import { fetchAd } from '@intelligence/services/common';

export interface UseAdOptions {
    /** 当前页面 URL（带 /v2 等 base，如 /v2/intelligence/following）；不传则请求所有页面的广告位 */
    url?: string;
    /** 广告位类型，如 'marquee'、'newicon' */
    type?: string;
    /** 是否启用，默认 true。false 时不发请求且 list 置空 */
    enabled?: boolean;
    /** 额外刷新标识，变化时强制重新请求 */
    refreshKey?: string;
    /** 刷新前是否清空旧列表，默认 true */
    clearBeforeFetch?: boolean;
}

export interface UseAdResult {
    /** 是否正在请求 */
    loading: boolean;
    /** 命中的广告列表（已按 base_url + 投放时间过滤） */
    list: any[];
    /** 主动重新拉取 */
    refresh: () => void;
}

const AD_LIST_CACHE: Record<string, any[]> = {};

const getAdCacheKey = (url?: string, type?: string): string => `${type || 'all'}__${url || 'all'}`;

const isSameAdList = (prevList: any[], nextList: any[]): boolean => JSON.stringify(prevList || []) === JSON.stringify(nextList || []);

/** 占位符格式：{变量名}，变量名对应 content 中的同名字段（数组），缺省用 * 兜底；支持多个占位符 */
const PLACEHOLDER_REGEX = /\{(\w+)\}/g;

interface IBaseUrlPlaceholderRule {
    keys: string[];
    templateUrl: string;
}

/** 解析 base_url 中的 {key} 占位符（同名占位符只保留一份） */
const parseBaseUrlPlaceholders = (baseUrl: string): IBaseUrlPlaceholderRule | null => {
    const matches = Array.from(baseUrl.matchAll(PLACEHOLDER_REGEX));
    if (!matches.length) return null;
    const keys = Array.from(new Set(matches.map((match) => match[1])));
    return { keys, templateUrl: baseUrl };
};

/** 转义正则特殊字符 */
const escapeRegExp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const cartesianProduct = <T>(groups: T[][]): T[][] =>
    groups.reduce<T[][]>((acc, group) => acc.flatMap((prefix) => group.map((item) => [...prefix, item])), [[]]);

/** 从 content 读取占位符对应字段的枚举值；未配置或为空时回退为 * */
const getPlaceholderValues = (content: any, key: string): string[] => {
    const rawValues = content?.[key];
    if (!Array.isArray(rawValues)) return ['*'];
    const values = rawValues.map(String).map((item) => item.trim()).filter(Boolean);
    return values.length ? values : ['*'];
};

/** 将 base_url 模板按 content 中的枚举数组展开；多变量时做笛卡尔积组合 */
const buildExpandedUrl = (templateUrl: string, keys: string[], values: string[]): string => {
    let expandedUrl = templateUrl;
    keys.forEach((key, index) => {
        expandedUrl = expandedUrl.replace(new RegExp(`\\{${escapeRegExp(key)}\\}`, 'g'), values[index]);
    });
    return expandedUrl;
};

/** 把占位符模板归一化为通配路径，供 tab 分组等场景使用 */
export const normalizeBaseUrlTemplate = (baseUrl: string): string => baseUrl.replace(PLACEHOLDER_REGEX, '*');

/** 把 base_url 模板转成正则片段：* 匹配任意路径内容 */
const toBaseUrlRegExpBody = (baseUrl: string): string => {
    const starToken = '__AD_STAR__';
    return escapeRegExp(baseUrl.replace(/\*/g, starToken)).replace(new RegExp(starToken, 'g'), '.*');
};

const matchBaseUrl = (baseUrl: string, currentUrl: string): boolean => {
    if (!baseUrl.includes('*')) return baseUrl === currentUrl;
    const isPrefixStar = baseUrl.endsWith('/*');
    const patternBaseUrl = isPrefixStar ? baseUrl.slice(0, -2) : baseUrl;
    const re = new RegExp(`^${toBaseUrlRegExpBody(patternBaseUrl)}${isPrefixStar ? '(?:/.*)?' : ''}$`);
    return re.test(currentUrl);
};
const parseAdContent = (content: any): any => {
    if (!content) return {};
    if (typeof content !== 'string') return content;
    try {
        return JSON.parse(content);
    } catch (error) {
        console.debug('[useAd] invalid content json:', error);
        return {};
    }
};

const normalizeAdItem = (item: any): any => ({
    ...(item || {}),
    content: parseAdContent(item?.content),
});

/**
 * URL 匹配：
 * - 完全匹配：/v2/intelligence/topCharts/mobileGames/ActiveUsers
 * - 路径通配：/v2/intelligence/topCharts/mobileGames/gamesDetails/星号/stats（星号即 *）
 * - 前缀通配：/v2/intelligence/topCharts/mobileGames/星号
 * - 变量占位：base_url 中 {变量名} 对应 content 同名字段（数组），依次展开后匹配
 *   单变量示例：/v2/.../gamesDetails/{game_id}/stats
 *   多变量示例：/v2/.../gamesDetails/{game_id}/{tab}
 *   content 示例：{ "game_id": ["id1"], "tab": ["stats", "countries/activeusers"] }
 *   tab 值可含路径段（含 /），会原样拼入 base_url
 *   tab 为一级路径（无 /）时，未配置的子路由视为通配（如 countries 命中 countries/activeusers）
 */
export const isUrlMatch = (baseUrl: string, currentUrl: string, content?: any): boolean => {
    if (!baseUrl || !currentUrl) return false;

    const placeholderRule = parseBaseUrlPlaceholders(baseUrl);
    if (placeholderRule) {
        const valueGroups = placeholderRule.keys.map((key) => getPlaceholderValues(content, key));
        return cartesianProduct(valueGroups).some((values) => {
            const expandedUrl = buildExpandedUrl(placeholderRule.templateUrl, placeholderRule.keys, values);
            if (matchBaseUrl(expandedUrl, currentUrl)) return true;
            const tabIndex = placeholderRule.keys.indexOf('tab');
            if (tabIndex >= 0 && !String(values[tabIndex]).includes('/')) {
                return matchBaseUrl(`${expandedUrl}/*`, currentUrl);
            }
            return false;
        });
    }

    return matchBaseUrl(baseUrl, currentUrl);
};

/**
 * 过滤接口返回的广告列表：
 * - base_url 命中当前页面 URL（支持通配符）
 * - 当前时间在 start_time 和 end_time 之间（任一缺省视为不限）
 * - content 支持 JSON 字符串或对象，缺省时由消费方兜底
 */
const filterValidAds = (list: any[], url: string | undefined, type: string): any[] => {
    if (!Array.isArray(list) || !list.length) return [];
    const now = Date.now();
    return list.reduce<any[]>((acc, item) => {
        if (!item) return acc;
        // 1) 广告位类型命中；后端已按 type 过滤，这里兼容本地 mock 返回混合类型的场景
        if (item.type && item.type !== type) return acc;
        // 2) URL 命中；url 不传时返回所有页面配置，由消费方自行匹配
        if (url && !isUrlMatch(item.base_url, url, item.content)) return acc;
        // 3) 投放时间窗口判断（格式：YYYY-MM-DD HH:mm:ss）
        const start = item.start_time ? new Date(item.start_time).getTime() : NaN;
        const end = item.end_time ? new Date(item.end_time).getTime() : NaN;
        if (!Number.isNaN(start) && now < start) return acc;
        if (!Number.isNaN(end) && now > end) return acc;
        return [...acc, normalizeAdItem(item)];
    }, []);
};

/**
 * 广告位获取 hook。
 * - url / type 变化时自动重新请求
 * - 内部做了竞态保护：参数变化或组件卸载后，旧请求结果会被丢弃
 */
export function useAd(options: UseAdOptions): UseAdResult {
    const { url, type, enabled = true, refreshKey, clearBeforeFetch = true } = options;
    const cacheKey = getAdCacheKey(url, type);
    const initialList = !clearBeforeFetch ? AD_LIST_CACHE[cacheKey] || [] : [];
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState<any[]>(initialList);
    /** 自增请求 id：每次新请求 +1，回填时只接受最新一次的结果 */
    const reqIdRef = useRef(0);
    const listRef = useRef<any[]>(initialList);

    const updateList = (nextList: any[]) => {
        AD_LIST_CACHE[cacheKey] = nextList;
        if (isSameAdList(listRef.current, nextList)) return;
        listRef.current = nextList;
        setList(nextList);
    };

    const run = async () => {
        // 跑马灯默认切页时清空旧数据；newicon 等场景保留旧列表，避免标签刷新闪烁
        if (clearBeforeFetch) {
            updateList([]);
        }
        if (!enabled || !type) return;
        const myReqId = ++reqIdRef.current;
        setLoading(true);
        try {
            const res = await fetchAd({ ...(url ? { base_url: url } : {}), type });
            if (myReqId !== reqIdRef.current) return;
            const raw = res?.code === 0 && Array.isArray(res?.data) ? res.data : [];
            updateList(filterValidAds(raw, url, type));
        } catch (e) {
            console.debug('[useAd] failed:', e);
            if (myReqId === reqIdRef.current && clearBeforeFetch) updateList([]);
        } finally {
            if (myReqId === reqIdRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        run();
        return () => {
            reqIdRef.current++;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, type, enabled, refreshKey, clearBeforeFetch]);

    return { loading, list, refresh: run };
}
